import { Box } from '@ace-clublabs/shared-component-library';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorPage from '@/components/shared/ErrorPage';
import LoadingContainer from '@/components/shared/LoadingContainer';
import { constants } from '@/constants';
import EmailInquiryForm, {
  type HandleFormSubmitData,
} from '@/features/appointments/components/EmailInquiryForm';
import { postProcessEmailInquiry } from '@/features/confirmation/api/postCreateEmailLead';
import { buildAdditionalNotes } from '@/features/confirmation/utils/buildAdditionalNotes';
import type { ProcessEmailInquiryData } from '@/features/confirmation/utils/ProcessEmailInquiryData';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import useLandingPageSetup from '@/hooks/useLandingPageSetup';
import { getClubCode, Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import { selectTrvEndLocationForTagging } from '@/lib/tagging/utils';
import useAppointmentStore from '@/store/AppointmentStore';
import useContextAwarenessStore from '@/store/ContextAwarenessStore';
import useIdentityStore from '@/store/IdentityStore';
import useProgressStore from '@/store/ProgressStore';
import useSessionStore from '@/store/SessionStore';
import useTSTStore from '@/store/TSTStore';
import { FlowType } from '@/types/FlowType';

function EmailInquiry() {
  const navigateTo = useLandingPageRedirection();

  const { setProgress } = useProgressStore();
  const hasTagged = useRef(false);

  const [isProcessingEmailInquiry, setIsProcessingEmailInquiry] =
    useState(false);
  const [processEmailInquiryFailed, setProcessEmailInquiryFailed] =
    useState(false);
  const handleProcessEmailInquirySuccess = () => {
    setProgress(75);
  };

  const handleIsCreatingEmailInquiry = () => {
    setIsProcessingEmailInquiry(true);
  };

  const handleProcessEmailInquiryError = () => {
    setIsProcessingEmailInquiry(false);
    setProcessEmailInquiryFailed(true);
  };
  const navigate = useNavigate();

  const { sessionSource, sessionId } = useSessionStore();

  const {
    selectedDestination,
    searchedDestination,
    userSearch,
    contactInfo,
    setConfirmationId,
  } = useAppointmentStore();
  const trvEndLocation = useAppointmentStore(selectTrvEndLocationForTagging);

  const userInfo = useIdentityStore((state) => state.userInfo);

  const tagging = useTagging();

  const { destinationsQuery } = useLandingPageSetup(FlowType.EMAIL);

  const isDestinationsLoading =
    destinationsQuery.isPending ||
    destinationsQuery.error?.message === constants.TANSTACK_ABORTED_ERROR;

  useEffect(() => {
    if (hasTagged.current) return;
    if (!sessionId || !sessionSource) return;

    tagging.tag('view', sessionSource, {
      page: Page.contactInfo,
      sessionid: sessionId,
      flow: 'emailInquiry',
      trv_end_location: trvEndLocation,
      comm_pref: 'email',
    });

    hasTagged.current = true;
  }, [sessionSource, sessionId, trvEndLocation, tagging.tag]);

  const { TSTParams } = useTSTStore();
  const contextAwareness = useContextAwarenessStore(
    (state) => state.contextAwareness,
  );

  const processEmailInquiry = useMutation({
    mutationFn: (emailData: ProcessEmailInquiryData) =>
      postProcessEmailInquiry(emailData),
    onSuccess: (responseData) => {
      setConfirmationId(responseData.incidentId);
      handleProcessEmailInquirySuccess();
      setTimeout(() => {
        navigate('/email-confirmation', { replace: true });
      }, 250);
    },
    onError: (err) => {
      if (err.message !== constants.TANSTACK_ABORTED_ERROR)
        handleProcessEmailInquiryError();
    },
  });

  const handleFormSubmit = (data: HandleFormSubmitData) => {
    handleIsCreatingEmailInquiry();

    const destinationNote =
      data.searchedDestination ||
      data.userSearch ||
      searchedDestination ||
      userSearch;

    const emailData: ProcessEmailInquiryData = {
      clubCode: getClubCode(),
      destination:
        data.selectedDestination.destinationApiName ??
        selectedDestination.destinationApiName ??
        '',
      email: contactInfo.email ?? '',
      firstName: contactInfo.firstName ?? '',
      lastName: contactInfo.lastName ?? '',
      phone: contactInfo.phoneNumber ?? '',
      emailMessage: buildAdditionalNotes({
        destinationNote,
        userNotes: contactInfo.emailMessage ?? '',
        card: contextAwareness.card,
        sourceUrl: contextAwareness.sourceUrl,
      }),
      whenDoYouPlanToTravel: contactInfo.expectedTravelTime ?? '',
      zipCode: contactInfo.zipCode ?? '',
      typeOfTripDesired: contactInfo.typeOfTrip ?? [],
      AAAMemberFlag: !!userInfo?.customerId,
      membershipNo: userInfo?.memberNumber ?? '',
      trafficSource: window.aceMediaTagValues.traffic_source ?? '',
      trafficSourceCategory: window.aceMediaTagValues.traffic_category ?? '',
      websiteSource: window.aceMediaTagValues.website_source ?? '',
      MACSCustId: userInfo?.customerId || '',
      TSTTripURL: TSTParams.TSTTripURL,
      preferredContactMethod: contactInfo.communicationPreference ?? '',
    };
    processEmailInquiry.mutate(emailData);
  };

  if (isDestinationsLoading) {
    const loadingMessage = 'Your advisor app is loading...';
    return <LoadingContainer message={loadingMessage} showRadios={false} />;
  }

  if (isProcessingEmailInquiry) {
    return <LoadingContainer message={'Sending your request...'} />;
  }

  if (processEmailInquiryFailed) {
    return <ErrorPage onClickTryAgain={() => navigate(navigateTo)} />;
  }

  return (
    <Box
      sx={[
        {
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'background.default',
        },
      ]}
    >
      <EmailInquiryForm onSubmit={handleFormSubmit} />
    </Box>
  );
}

export default EmailInquiry;
